import {
  Box,
  Button,
  DarkMode,
  Flex,
  Stack,
  Text,
  Wrap,
} from "@chakra-ui/react";
import { ApiErrorProvider } from "@raiden/library/components/ReactHookForm/ApiError";
import { RENTAL_AREA_STATES_VALUE_ENABLED } from "@raiden/library/constants/rentalArea";
import { useApi } from "@raiden/library/hooks/useApi";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { FormattedMessage, useIntl } from "react-intl";
import { CenteredContent } from "../../components/CenteredContent";
import { DataHandler } from "../../components/DataHandler";
import { Href } from "../../components/Href";
import { PageLayout } from "../../components/PageLayout";
import { Splash } from "../../components/Splash";
import { CONTENT_WIDTH } from "../../constants/Layout";
import { CommunityHallsCard } from "../../components/CommunityHalls/Card";
import { ContactsCreateForm } from "../Contacts/Create/Form";
import ContactsCreateHandler from "../Contacts/Create/Handler";
import { PostsContentView } from "../Posts/Common/ContentView";
import { PersonnalInformation } from "./Information";
// import { Map } from "./Map";

const Map = dynamic(() => import("./Map").then((module) => module.Map), {
  ssr: false,
});

/**
 * @typedef {Object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post>>} swrResponsePost
 */
/**
 * @param {Props} props
 */
export function ContactUs({ swrResponsePost }) {
  const intl = useIntl();

  const [isSuccess, setIsSuccess] = useState(false);

  const { configuration } = useConfiguration();

  const environment = configuration.environments?.[0];

  const [customersContactCategories] = useState(
    configuration.customers_contacts?.categories,
  );

  const breadcrumbItems = useMemo(() => {
    return [
      {
        title: intl.formatMessage({ defaultMessage: "Accueil" }),
        url: generateFrontPath({ id: "@front.home" }),
      },
      {
        title: intl.formatMessage({ defaultMessage: "Nous contacter" }),
        url: generateFrontPath({ id: "@front.contact" }),
      },
    ];
  }, [intl]);

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/RentalArea").RentalArea[]>} */
  const { swrResponse: swrResponseRentalAreas } = useApi(
    generateApiUrl({
      id: "@api.rental_areas.viewAny",
      query: {
        fields: ["rental_area.covers"],
        state: [RENTAL_AREA_STATES_VALUE_ENABLED],
        per_page: 9,
      },
    }),
  );

  const handleSuccess = useCallback(() => {
    setIsSuccess(true);
  }, []);

  const handleResetForm = useCallback(() => {
    setIsSuccess(false);
  }, []);

  return (
    <PageLayout
      breadcrumbItems={breadcrumbItems}
      title={intl.formatMessage({ defaultMessage: "Nous contacter" })}>
      <Stack spacing="24px">
        <Box backgroundColor="brandPrimary.700">
          <CenteredContent maxWidth="1920px">
            <Flex direction={{ base: "column", lg: "row" }}>
              <Box flexShrink={0} px="16px">
                <Box
                  w={{
                    base: "initial",
                    lg: `calc((min(100dvw, 1920px) - min(min(100dvw, 1920px), ${CONTENT_WIDTH})) / 2 + 300px)`,
                  }}
                  pl={`calc((min(100dvw, 1920px) - min(min(100dvw, 1920px), ${CONTENT_WIDTH})) / 2 - 16px)`}
                  py="24px">
                  <DarkMode>
                    <Stack spacing="0.75rem">
                      <PersonnalInformation environment={environment} />
                    </Stack>

                    {/* TODO: Valeurs écrites en dur, en attendant de trouver une solution pour les configurer sur l'admin */}

                    <Stack mt="1.5rem" spacing="1rem">
                      <Text variant="h3" fontWeight={500}>
                        <FormattedMessage defaultMessage="Permanences d'urgence" />
                      </Text>

                      <Stack spacing="0.25rem">
                        <Text fontSize="1rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="Services voirie" />
                        </Text>

                        <Text fontSize="0.875rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="06 07 96 81 26" />
                        </Text>
                      </Stack>

                      <Stack spacing="0.25rem">
                        <Text fontSize="1rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="Services des eaux" />
                        </Text>

                        <Text fontSize="0.875rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="06 08 23 56 53" />
                        </Text>
                      </Stack>

                      <Stack spacing="0.25rem">
                        <Text fontSize="1rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="Services bâtiments" />
                        </Text>

                        <Text fontSize="0.875rem" fontWeight={500}>
                          <FormattedMessage defaultMessage="06 08 96 51 26" />
                        </Text>
                      </Stack>
                    </Stack>
                  </DarkMode>
                </Box>
              </Box>

              <Flex justify="stretch" flexGrow={1}>
                <Box w="full" h="full">
                  <Map />
                </Box>
              </Flex>
            </Flex>
          </CenteredContent>
        </Box>

        <Box px="16px">
          <CenteredContent>
            <Stack spacing="16px">
              <Text variant="h2">
                <FormattedMessage defaultMessage="Nous écrire" />
              </Text>

              <Box>
                {isSuccess ? (
                  <Box pb="32px">
                    <Splash
                      title={intl.formatMessage({
                        defaultMessage: "Nous avons bien reçu votre message",
                      })}
                      description={intl.formatMessage({
                        defaultMessage:
                          "Nous vous remercions et reviendrons vers vous au plus vite",
                      })}
                      src={generateFrontPath({
                        id: "@front.internal-assets",
                        parameters: {
                          filePath: "illustrations/contact-success.svg",
                        },
                        includeBasePath: true,
                      })}
                      addon={
                        <Wrap justify="center" spacing="16px">
                          <Button
                            onClick={handleResetForm}
                            variant="outline"
                            colorScheme="brandPrimary"
                            borderColor="brandPrimary.700">
                            <Text variant="button" color="brandPrimary.700">
                              <FormattedMessage defaultMessage="Retourner au formulaire" />
                            </Text>
                          </Button>

                          <Href
                            href={generateFrontPath({
                              id: "@front.home",
                            })}>
                            <Button
                              colorScheme="brandPrimary"
                              backgroundColor="brandPrimary.700">
                              <Text variant="button" color="inherit">
                                <FormattedMessage defaultMessage="Aller à la page d'accueil" />
                              </Text>
                            </Button>
                          </Href>
                        </Wrap>
                      }
                    />
                  </Box>
                ) : (
                  <>
                    {customersContactCategories && (
                      <GoogleReCaptchaProvider
                        reCaptchaKey={
                          process.env.NEXT_PUBLIC_RECAPTCHA_V3_PUBLIC_KEY ?? ""
                        }>
                        <ApiErrorProvider>
                          <ContactsCreateHandler
                            customersContactCategories={
                              customersContactCategories
                            }
                            onSuccess={handleSuccess}>
                            {({ form, isLoading }) => (
                              <ContactsCreateForm
                                control={form.control}
                                isLoading={isLoading}
                                customersContactCategories={
                                  customersContactCategories
                                }
                              />
                            )}
                          </ContactsCreateHandler>
                        </ApiErrorProvider>
                      </GoogleReCaptchaProvider>
                    )}
                  </>
                )}
              </Box>
            </Stack>
          </CenteredContent>
        </Box>

        {(swrResponseRentalAreas.data?.data ?? []).length > -1 && (
          <Box px="16px">
            <CenteredContent>
              <Text variant="h2">
                <FormattedMessage defaultMessage="Les salles communales" />
              </Text>

              <DataHandler swrResponse={swrResponseRentalAreas}>
                {({ data: rentalAreas }) => (
                  <Flex
                    mt="1rem"
                    direction={{ base: "column", md: "row" }}
                    wrap="wrap"
                    gap="1rem">
                    {rentalAreas.map((rentalArea) => (
                      <CommunityHallsCard
                        rentalArea={rentalArea}
                        key={rentalArea.id}
                      />
                    ))}
                  </Flex>
                )}
              </DataHandler>
            </CenteredContent>
          </Box>
        )}

        {swrResponsePost.data?.data && (
          <DataHandler swrResponse={swrResponsePost}>
            {({ data: post }) => (
              <Box backgroundColor="brandPrimary.50" py="24px">
                <Box px="16px">
                  <CenteredContent>
                    <Text variant="h2">
                      <FormattedMessage defaultMessage="Contacts" />
                    </Text>
                  </CenteredContent>
                </Box>

                <Box mt="24px">
                  <CenteredContent
                    maxWidth={({ defaultMaxWidth }) =>
                      `calc(${defaultMaxWidth} + 32px)`
                    }>
                    <PostsContentView post={post} />
                  </CenteredContent>
                </Box>
              </Box>
            )}
          </DataHandler>
        )}
      </Stack>
    </PageLayout>
  );
}
